import { useState } from "react";
import type { StepDetailProps } from "./index";
import { STEP_OUTPUT_SCHEMAS, zodIssuesToErrors } from "./schemas";
import { StepActions } from "./StepActions";

interface ChecklistItem {
  id: string;
  label: string;
  done?: boolean;
}

function readChecklist(inputs: Record<string, unknown>): ChecklistItem[] {
  const raw = inputs["checklist"];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is ChecklistItem => {
      if (!c || typeof c !== "object") return false;
      const r = c as Record<string, unknown>;
      return typeof r["id"] === "string" && typeof r["label"] === "string";
    })
    .map((c) => ({ id: c.id, label: c.label, done: Boolean(c.done) }));
}

export function MilestoneDetail({
  step,
  play,
  workspaceId,
  currentUserId,
  persona,
}: StepDetailProps) {
  const items = readChecklist(step.inputs);
  const [checked, setChecked] = useState<Set<string>>(
    () => new Set(items.filter((i) => i.done).map((i) => i.id)),
  );
  const [notes, setNotes] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const buildOutput = (): Record<string, unknown> | null => {
    const payload: Record<string, unknown> = {
      completed_items: Array.from(checked),
    };
    if (notes) payload["notes"] = notes;
    const parsed = STEP_OUTPUT_SCHEMAS["milestone"].safeParse(payload);
    if (!parsed.success) {
      const map: Record<string, string> = {};
      for (const e of zodIssuesToErrors(parsed.error)) map[e.path] = e.message;
      setErrors(map);
      return null;
    }
    // Server requires every checklist id to be present at completion. Provide
    // a UI-side hint so users don't get a 422 from /complete.
    if (Array.from(checked).length < items.length) {
      setErrors({
        completed_items: "Tick every exit criterion to close the milestone",
      });
      return null;
    }
    setErrors({});
    return parsed.data as Record<string, unknown>;
  };

  return (
    <div className="ho-detail ho-milestone" data-testid="step-detail-milestone">
      <div className="ho-eb">MILESTONE</div>
      <h2 className="ho-h2">{step.title}</h2>
      <div className="ho-ms-crit" data-testid="milestone-checklist">
        <div className="ho-ms-h">EXIT CRITERIA</div>
        {items.length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>
            No exit criteria.
          </div>
        ) : (
          items.map((c) => (
            <div
              key={c.id}
              className={`ho-ms-c${checked.has(c.id) ? " done" : ""}`}
              data-testid={`milestone-item-${c.id}`}
            >
              <input
                type="checkbox"
                checked={checked.has(c.id)}
                onChange={() => toggle(c.id)}
                disabled={step.state !== "running"}
                style={{ marginRight: 6 }}
              />
              <span>{c.label}</span>
            </div>
          ))
        )}
      </div>
      {step.state === "running" ? (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          <label>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--text-3)",
                letterSpacing: ".14em",
              }}
            >
              NOTES (optional)
            </span>
            <textarea
              data-testid="milestone-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              style={{
                marginTop: 4,
                width: "100%",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 8,
                fontFamily: "inherit",
                fontSize: 13,
              }}
            />
          </label>
        </div>
      ) : null}
      {errors["completed_items"] ? (
        <span
          style={{ color: "var(--danger)", fontSize: 12 }}
          data-testid="milestone-error"
        >
          {errors["completed_items"]}
        </span>
      ) : null}
      <StepActions
        step={step}
        workspaceId={workspaceId}
        playId={play.id}
        currentUserId={currentUserId}
        persona={persona}
        buildOutput={buildOutput}
      />
    </div>
  );
}
